import type { BitmapLayout } from './layout-cache'
import { BITMAP_TILE_RGB } from './gl-renderer'

export type Canvas2dFrame = {
 lightBackground?: boolean
	size: number
	layout: BitmapLayout
	/** Overall entry progress 0..1; 1 draws the finished bitmap. */
	progress: number
	startTime: number
	now: number
	mouse: { x: number; y: number } | null
	mobile: boolean
}

/**
 * Canvas 2D twin of the WebGL shader for devices without WebGL2 (or when the
 * frame-rate monitor steps a tile down). Same timing, easing and repulsion
 * maths, so a tile that changes renderer mid-life does not visibly change.
 */
export function drawCanvas2dFrame(ctx: CanvasRenderingContext2D, f: Canvas2dFrame) {
	const { size: canvasSize, layout, progress, startTime, now, mouse, mobile } = f
	const [R, G, B] = BITMAP_TILE_RGB
	ctx.fillStyle = f.lightBackground ? '#ffffff' : '#0d1117'
	ctx.fillRect(0, 0, canvasSize, canvasSize)
	const grid = canvasSize / Math.max(layout.layoutWidth, layout.usedHeight)
	const offsetY = (canvasSize - layout.usedHeight * grid) / 2
	const pad = grid / 4
	const inst = layout.instances
	const count = layout.count

	for (let n = 0; n < count; n++) {
		const o = n * 4
		const r = inst[o + 2]
		let gx = inst[o]
		let gy = inst[o + 1]
		let shrink = 1
		let glow = 0

		if (mouse && progress === 1) {
			const mx = mouse.x / grid
			const my = (mouse.y - offsetY) / grid
			const dx = gx + r / 2 - mx
			const dy = gy + r / 2 - my
			const dist = Math.sqrt(dx * dx + dy * dy)
			const radius = mobile ? 10 : 18
			if (dist < radius && dist > 0.001) {
				const e = Math.max(0, Math.min(1, dist / radius))
				const s = e * e * (3 - 2 * e)
				if (r === 1) {
					if (!mobile) glow = 1 - s
				} else {
					const force = (1 - dist / radius) * 5
					gx += (dx / dist) * force
					gy += (dy / dist) * force
					shrink = s
					if (r <= 4) {
						glow = 1 - shrink
						shrink = Math.max(shrink, 0.5)
					}
				}
			}
		}

		const angle = Math.sin(1234.56 * n) * Math.PI * 2
		const dist0 = (canvasSize / grid) * (1.2 + 0.5 * Math.cos(789.1 * n))
		const sx = inst[o] + Math.cos(angle) * dist0
		const sy = inst[o + 1] + Math.sin(angle) * dist0
		const elapsed = now - startTime - ((n / count) * 1200 + 100 * Math.abs(Math.sin(n)))
		const duration = 1400 + 50 * r
		const p = progress === 1 ? 1 : Math.max(0, Math.min(1, elapsed / duration))
		if (p <= 0) continue

		const eased = 1 + 2.70158 * Math.pow(p - 1, 3) + 1.70158 * Math.pow(p - 1, 2)
		const cx = sx + (gx - sx) * eased
		const cy = sy + (gy - sy) * eased
		let pw = r * grid - 2 * pad
		const shrunk = pw * shrink
		const shrinkOffset = (pw - shrunk) * 0.5
		const px = cx * grid + pad + shrinkOffset
		const py = cy * grid + offsetY + pad + shrinkOffset
		pw = shrunk
		if (pw <= 0) continue

		ctx.globalAlpha = Math.min(1, 1.5 * p)
		const bright = 1 + 1.2 * glow
		ctx.fillStyle = `rgb(${Math.min(255, R * bright)}, ${Math.min(255, G * bright)}, ${Math.min(
			255,
			B * bright
		)})`
		if (!mobile && glow > 0.01) {
			ctx.shadowBlur = 8 * glow
			ctx.shadowColor = `rgba(${R}, ${G}, ${B}, ${0.9 * glow})`
		} else {
			ctx.shadowBlur = 0
		}
		ctx.fillRect(px, py, pw, pw)
	}
	ctx.globalAlpha = 1
	ctx.shadowBlur = 0
}
